import { useMutation } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { nanoid } from 'nanoid';
import { useEffect, useMemo, useRef } from 'react';
import useState from 'react-usestateref';

import { checkSharpness } from '../services/sharpness';
import { createTask, deleteTask } from '../services/tasks';

export interface IImage {
  /** unique id to identify the image, can be use as a key */
  id: string;

  /** base64 image data */
  data: string;

  /** is validation failed */
  isValid: boolean;

  /** is image validating */
  isValidating: boolean;

  error?: string;

  /** underlying id in backend */
  taskId?: string;
}

interface ITaskCreate {
  batchId: string;
  image: string;
}

interface ITaskDelete {
  batchId: string;
  taskId: string;
}

export const useImageUpload = (maxLimit: number = 0) => {
  const [_, setImages, imagesRef] = useState<IImage[]>([]);
  const hasReachedMaxLimit = useRef(false);

  // validate uploaded image
  const imageValidationMutation = useMutation({
    mutationFn: (image: string) => checkSharpness(image),
  });

  // create task
  const createTaskMutation = useMutation({
    mutationFn: ({ batchId, image }: ITaskCreate) => createTask({ batchId, image }),
  });

  // delete task
  const deleteTaskMutation = useMutation({
    mutationFn: ({ batchId, taskId }: ITaskDelete) => deleteTask({ batchId, taskId }),
  });

  const count = useMemo(
    () => imagesRef.current.filter(({ isValid }) => isValid).length,
    [imagesRef.current],
  );

  useEffect(() => {
    hasReachedMaxLimit.current = count >= maxLimit;
  }, [count]);

  const clear = () => {
    setImages([]);
  };

  const validate = async (data: string) => {
    const state = { isValid: false, parsedData: data, error: '' };
    try {
      const response = await imageValidationMutation.mutateAsync(data);
      state.isValid = (response as any)?.valid as boolean;
      state.parsedData = (response as any)?.imageString ?? data;
    } catch (_error) {
      const error = _error as HTTPError;
      if (!error.response.ok) {
        const errorMessage = await error.response.json<{ err: string }>();
        state.error = errorMessage.err;
      }
    }
    return state;
  };

  const uploadFile = async ({ batchId, image }: ITaskCreate) => {
    try {
      const task = await createTaskMutation.mutateAsync({ image, batchId });
      return { isUploaded: true, task };
    } catch (error) {
      return { isUploaded: false, task: null };
    }
  };

  const remove = (id: string) => {
    const modifiedImageArray = imagesRef.current.filter((image) => image.id !== id);
    setImages(modifiedImageArray);
  };

  const add = async ({
    data,
    batchId,
    bypassValidation,
  }: {
    data: string;
    batchId: string;
    bypassValidation: boolean;
  }) => {
    const id = nanoid();

    // initial image state
    const image: IImage = { id, data, isValid: false, isValidating: true };
    setImages([image, ...imagesRef.current]);

    const { isValid, parsedData, error } = bypassValidation
      ? { isValid: true, parsedData: data, error: '' }
      : await validate(data);

    // if isValid false, skip the task creation
    // if isValid true, create a task; however if task failed we set isValid to false
    let isUploaded = isValid;
    let task = null;
    if (isValid) {
      const res = await uploadFile({ image: parsedData, batchId });
      isUploaded = res.isUploaded;
      task = res.task;
    }

    // remove previously updated image from array before we update the new state of the image
    remove(id);
    setImages([
      {
        ...image,
        error,
        taskId: task?.id,
        data: parsedData,
        isValid: isUploaded,
        isValidating: false,
      },
      ...imagesRef.current,
    ]);
  };

  const destroy = async ({
    taskId,
    imageId,
    batchId,
  }: {
    imageId: string;
    taskId: string;
    batchId: string;
  }) => {
    await deleteTaskMutation.mutateAsync({ taskId, batchId });
    remove(imageId);
  };

  return {
    add,
    clear,
    remove: destroy,
    count,
    images: imagesRef.current,
    hasReachedMaxLimit: hasReachedMaxLimit,
  };
};
